const origin = window.location.origin;
const defaultUrl = document.querySelector('[data-event-center-data-page]').getAttribute('data-event-center-data-page');
const FETCH_URL = `${origin}${defaultUrl}?view=events-data-template`;
const defaultSort = document.querySelector('[data-default-sort]').getAttribute('data-default-sort');

// Configuration object for the event grid
const CONFIGURATIONS = {
  defaultSort: defaultSort || 'none'
}


/**
 * ResourceFilter - A comprehensive resource filtering, sorting, and pagination system
 * 
 * @class ResourceFilter
 * @description Manages dynamic resource display with filtering by board_council,
 * sorting capabilities, and paginated results. Fully accessible with WCAG 2.1 AA compliance.
 * 
 * Features:
 * - Dynamic resource loading via fetch API
 * - Multi-criteria filtering (board_council)
 * - Multiple sorting options (A-Z, Z-A, date-desc)
 * - Pagination with configurable items per page
 * - Smooth animations and transitions
 * - Full keyboard navigation support
 * - Screen reader accessibility
 * - Responsive design for mobile and desktop
 * 
 * @example
 * // Initialize automatically on DOM ready
 * const resourceFilter = new ResourceFilter();
 * 
 * // Or access the global instance
 * window.resourceFilter.applyFilters();
 * 
 * @version 1.0.0
 * @author Symmetry Commerce
 * @since 2024
 */
class EventFilter {
    /**
     * Creates a new ResourceFilter instance
     * @constructor
     * @description Initializes the resource filter system with default settings
     * and begins the initialization process
     */
    constructor() {
      /** @type {Array} Original unfiltered resource data */
      this.originalEvents = [];
      
      /** @type {Array} Currently filtered resource data */
      this.filteredEvents = [];
      
      /** @type {boolean} Whether filtering functionality is enabled */
      this.filtersEnabled = false;
      
      /** @type {boolean} Whether sorting functionality is enabled */
      this.sortingEnabled = false;
  
      /** @type {boolean} Whether filters are currently applied */
      this.filtersApplied = false;
      
      /** @type {Object} Current filter selections */
      this.currentFilters = {
        board_council: ''
      };

      this.filterElements = document.querySelectorAll('[data-filter-type="board_council"]');
      this.originalFilterData = {
        board_council: []
      };
      
      /** @type {string} Current sort method */
      this.currentSort = CONFIGURATIONS.defaultSort || 'none';
      
      /** @type {number} Current page number (1-based) */
      this.currentPage = 1;
      
      /** @type {number} Number of events to display per page */
      this.eventsPerPage = 16;
      
      /** @type {number} Total number of pages */
      this.totalPages = 1;
      
      /** @type {HTMLElement|null} Pagination controls container */
      this.paginationContainer = null;
  
      //console.log('EventFilter initializing...');
      this.init();
    }
  
    /**
     * Initializes the ResourceFilter system
     * @async
     * @description Orchestrates the complete initialization process including data loading,
     * pagination setup, event binding, and initial rendering
     * @returns {Promise<void>} Resolves when initialization is complete
     */
    async init() {
      await this.loadEventData();
      this.loadFilterData();
      this.initializePagination();
      this.bindFilterEvents();
      this.bindSortEvents();
      this.renderAllEvents();
      //console.log('EventFilter initialized successfully');
    }
  
    loadFilterData(resources = this.originalEvents) {
      // console.log('Loading filter data...');
      // console.log('filterElements:', this.filterElements);
      this.filterElements.forEach(select => {
        // console.log('select:', select);
        let filterType = select.getAttribute('data-filter-type');
        this.originalFilterData[filterType] = Array.from(select.querySelectorAll('option')).map(option => option.value);
  
        const uniqueOptions = [...new Set(resources.map(resource => resource[filterType]))];
  
        this.createFilterOptions(select, uniqueOptions);
      });
    }
  
    createFilterOptions(select, uniqueOptions) {
      select.innerHTML = '';
  
      if(uniqueOptions.length === 0) {
        return;
      }
  
      // console.log('Unique options:', uniqueOptions);
      const firstOption = document.createElement('option');
      firstOption.value = '';
      firstOption.textContent = select.getAttribute('data-filter-placeholder');
      select.appendChild(firstOption);
      
      uniqueOptions.forEach(option => {
        const newOption = document.createElement('option');
        newOption.value = option;
        newOption.textContent = option;
        select.appendChild(newOption);
      });
    }
  
  
    /**
     * Loads resource data from the API endpoint
     * @async
     * @description Fetches resource data and stores it in originalResources.
     * Handles errors gracefully by setting empty array on failure.
     * @returns {Promise<void>} Resolves when data loading is complete
     */
    async loadEventData() {
      try {
        this.originalEvents = await this.fetchEventData() || [];
        //console.log('Loaded events:', this.originalEvents.length);
      } catch (error) {
        console.error('Error fetching resource data:', error);
        this.originalEvents = [];
      }
    }
  
    /**
     * Fetches resource data from the server
     * @async
     * @private
     * @description Makes HTTP request to the resources data endpoint and returns parsed JSON
     * @returns {Promise<Array>} Array of resource objects, or empty array on error
     * @throws {Error} HTTP errors or network failures
     */
    async fetchEventData() {
      try {
        //console.log('Fetching event data from endpoint...');
        const response = await fetch(FETCH_URL);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
        //console.log('Event data:', data);
        return data.events || [];
      } catch (error) {
        console.error('Error fetching event data:', error);
        this.originalEvents = [];
      }
    }
  
    /**
     * Binds event listeners to filter controls
     * @description Finds all filter select elements and attaches change event listeners.
     * Also adds accessibility attributes if not already present.
     * Disables filtering if no filter elements are found.
     * @returns {void}
     */
    bindFilterEvents() {
      const filterSelects = document.querySelectorAll('[data-filter-type]');
  
      if(filterSelects.length === 0) {
        this.filtersEnabled = false;
        //console.log('No filter selects found, disabling filters');
        return;
      }
  
      //console.log('Found filter selects:', filterSelects.length);
      
      filterSelects.forEach(select => {
        //console.log('Binding event to:', select.id, 'type:', select.getAttribute('data-filter-type'));
        
        // Add accessibility attributes
        if (!select.getAttribute('aria-label') && !select.getAttribute('aria-labelledby')) {
          const filterType = select.getAttribute('data-filter-type');
          select.setAttribute('aria-label', `Filter by ${filterType.replace('_', ' ')}`);
        }
        
        select.addEventListener('change', (e) => {
          const filterType = e.target.getAttribute('data-filter-type');
          const filterValue = e.target.value;
          
          //console.log('Filter changed:', filterType, '=', filterValue);
          this.currentFilters[filterType] = filterValue;
          this.applyFilters();
        });
      });
    }
  
    /**
     * Binds event listeners to sort controls
     * @description Finds the sort select element and attaches change event listener.
     * Also adds accessibility attributes if not already present.
     * Disables sorting if no sort element is found.
     * @returns {void}
     */
    bindSortEvents() {    
      const sortSelect = document.querySelector('[data-sort-type="sort_by"]');
  
      if(!sortSelect) {
          this.sortingEnabled = false;
          //console.log('No sort select found, disabling sorting');
          return;
      }
  
      //console.log('Found Sort select: ', sortSelect);
  
      // Add accessibility attributes
      if (!sortSelect.getAttribute('aria-label') && !sortSelect.getAttribute('aria-labelledby')) {
        sortSelect.setAttribute('aria-label', 'Sort events');
      }
  
      sortSelect.addEventListener('change', (e) => {
          const sortValue = e.target.value;
          //console.log('Sort value changed: ', sortValue);
  
          this.currentSort = sortValue;
          //console.log('Filtered Applied?: ', this.filtersApplied);
          //console.log('this.filteredEvents:', this.filteredEvents);
          const sortedEvents = this.applySort(this.filtersApplied ? this.filteredEvents : this.originalEvents);
  
          this.renderEvents(sortedEvents);
      })
    }
  
    /**
     * Applies current filter settings to the event list
     * @description Filters events based on board_council selections.
     * If no filters are applied, shows all resources. Automatically applies
     * current sorting and re-renders the display.
     * @returns {void}
     */
    applyFilters() {
      if(this.currentFilters.board_council === '') {
        //console.log('THIS IS RESETTING THE FILTERS');
          if(this.currentSort !== 'none') {
              const sortedEvents = this.applySort(this.originalEvents);
              this.renderEvents(sortedEvents);
          } else {
              // If no sort is selected, apply the default sort set in the configurations
              if(CONFIGURATIONS.defaultSort !== 'none') {
                // console.log('Applying default sort:', CONFIGURATIONS.defaultSort);
                this.currentSort = CONFIGURATIONS.defaultSort;
                const sortedEvents = this.applySort(this.originalEvents);
                this.renderEvents(sortedEvents);
              } else {
                this.renderEvents(this.originalEvents);
              }
          }
          this.filtersApplied = false;
          return;
      }
  
      //console.log('Applying filters:', this.currentFilters);
      let filteredEvents = [...this.originalEvents];

      // Apply board_council filter
      if (this.currentFilters.board_council !== '') {
        filteredEvents = filteredEvents.filter(event => 
          event.board_council === this.currentFilters.board_council
        );
        //console.log('After board_council filter:', filteredEvents.length);
        this.filtersApplied = true;
      }
      
      //console.log('filteredEvents:', filteredEvents);
      
  
      //console.log('Final filtered events:', filteredEvents.length);
      this.filteredEvents = filteredEvents;
      
      if(this.currentSort !== 'none') {
          const sortedEvents = this.applySort(filteredEvents);
          this.renderEvents(sortedEvents);
      } else {
          // If no sort is selected, apply the default sort set in the configurations
          if(CONFIGURATIONS.defaultSort !== 'none') {
            // console.log('Applying default sort:', CONFIGURATIONS.defaultSort);
            this.currentSort = CONFIGURATIONS.defaultSort;
            const sortedEvents = this.applySort(filteredEvents);
            this.renderEvents(sortedEvents);
          } else {
            this.renderEvents(filteredEvents);
          }
      }
    }
  
    /**
     * Applies sorting to a collection of resources
     * @param {Array} events - Array of event objects to sort
     * @description Sorts events based on currentSort setting. Supports:
     * - 'a-z': Alphabetical ascending by name
     * - 'z-a': Alphabetical descending by name  
     * - 'date-desc': Date ascending (oldest first)
     * - 'none': No sorting applied
     * @returns {Array} New sorted array of events
     * @example
     * const sorted = this.applySort(this.originalEvents);
     */
    applySort(events) {
      //console.log('Applying sort:', this.currentSort);
      let sortedEvents = [...events];
      if(this.currentSort === 'none') {
          return events;
      }
  
      switch(this.currentSort.toLowerCase()) {
          case 'a-z':
              //console.log('Sorting by a-z');
              sortedEvents.sort((a, b) => {
                  //console.log('a:', a.name);
                  //console.log('b:', b.name);
                  return a.name.localeCompare(b.name);
              });
              break;
          case 'z-a':
              //console.log('Sorting by z-a');
              sortedEvents.sort((a, b) => {
                  //console.log('a:', a.name);
                  //console.log('b:', b.name);
                  return b.name.localeCompare(a.name);
              });
              break;
          case 'date-desc':
              //console.log('Sorting by oldest');
              sortedEvents.sort((a, b) => {
                // If both dates exist, compare them
                if(this.isDate(a.date) && this.isDate(b.date)) {
                  return new Date(a.date) - new Date(b.date);
                }
                // If only a has a date, it should come first
                if(this.isDate(a.date)) {
                  //console.log('a has a date, it should come first', a);
                  return -1;
                }
                // If only b has a date, it should come first
                if(this.isDate(b.date)) {
                  //console.log('b has a date, it should come first', b);
                  return 1;
                }
                // If neither has a date, maintain original order
                return 0;
              })
              break;
          case 'date-asc':
              //console.log('Sorting by newest');
              sortedEvents.sort((a, b) => {
                return new Date(b.date) - new Date(a.date);
              });
              break;
          default:
              //console.log('Unknown sort type selected: ', this.currentSort);
              break;
      }
  
      //console.log('Sorted events:', sortedEvents);
      return sortedEvents;
    }
  
    /**
     * Renders a specific page of events
     * @param {Array} events - Array of all events to paginate
     * @param {number} pageNumber - Page number to render (1-based)
     * @description Calculates the subset of events for the given page,
     * generates HTML for each event card, and updates the grid container.
     * Also triggers entrance animations for the new cards.
     * @returns {void}
     */
    renderPage(events, pageNumber) {
      const startIndex = (pageNumber - 1) * this.eventsPerPage;
      const endIndex = startIndex + this.eventsPerPage;
      const pageEvents = events.slice(startIndex, endIndex);
  
      //console.log(`Rendering page ${pageNumber}: showing ${pageEvents.length} events`);
  
      const eventCards = pageEvents.map((event, index) => {
        return this.createEventCardHTML(event, startIndex + index + 1);
      }).join('');
  
      this.gridContainer.innerHTML = eventCards;
      
      // Trigger animations for the cards
      this.triggerFilteredCardAnimations();
    }
  
    /**
     * Updates the pagination controls based on current state
     * @param {number} totalEvents - Total number of events to paginate
     * @description Generates accessible pagination HTML with Previous/Next buttons,
     * page numbers, and ellipsis for large page counts. Includes full ARIA support
     * for screen readers. Hides pagination if only one page exists.
     * @returns {void}
     */
    updatePagination(totalEvents) {
      this.totalPages = Math.ceil(totalEvents / this.eventsPerPage);
      
      if (!this.paginationContainer) {
        console.error('Pagination container not found');
        return;
      }
  
      if (this.totalPages <= 1) {
        this.paginationContainer.innerHTML = '';
        return;
      }
  
      let paginationHTML = '<nav class="pagination" aria-label="Event pagination" role="navigation">';
      
      // Previous button
      if (this.currentPage > 1) {
        paginationHTML += `<button class="pagination__btn pagination__btn--prev" data-page="${this.currentPage - 1}" aria-label="Go to previous page, page ${this.currentPage - 1}">‹ Previous</button>`;
      }
  
      // Page numbers
      const startPage = Math.max(1, this.currentPage - 2);
      const endPage = Math.min(this.totalPages, this.currentPage + 2);
  
      if (startPage > 1) {
        paginationHTML += `<button class="pagination__btn" data-page="1" aria-label="Go to page 1">1</button>`;
        if (startPage > 2) {
          paginationHTML += `<span class="pagination__dots" aria-hidden="true">...</span>`;
        }
      }
  
      for (let i = startPage; i <= endPage; i++) {
        const isCurrentPage = i === this.currentPage;
        if (isCurrentPage) {
          paginationHTML += `<button class="pagination__btn pagination__btn--current" data-page="${i}" aria-current="page" aria-label="Current page, page ${i}">${i}</button>`;
        } else {
          paginationHTML += `<button class="pagination__btn" data-page="${i}" aria-label="Go to page ${i}">${i}</button>`;
        }
      }
  
      if (endPage < this.totalPages) {
        if (endPage < this.totalPages - 1) {
          paginationHTML += `<span class="pagination__dots" aria-hidden="true">...</span>`;
        }
        paginationHTML += `<button class="pagination__btn" data-page="${this.totalPages}" aria-label="Go to page ${this.totalPages}">${this.totalPages}</button>`;
      }
  
      // Next button
      if (this.currentPage < this.totalPages) {
        paginationHTML += `<button class="pagination__btn pagination__btn--next" data-page="${this.currentPage + 1}" aria-label="Go to next page, page ${this.currentPage + 1}">Next ›</button>`;
      }
  
      paginationHTML += '</nav>';
  
      this.paginationContainer.innerHTML = paginationHTML;
      this.bindPaginationEvents();
    }
  
    /**
     * Binds event listeners to pagination buttons
     * @private
     * @description Adds click and keyboard event listeners to all pagination buttons.
     * Handles page navigation, filter/sort preservation, smooth scrolling, and
     * focus management for accessibility.
     * @returns {void}
     */
    bindPaginationEvents() {
      const paginationButtons = this.paginationContainer.querySelectorAll('.pagination__btn');
      
      paginationButtons.forEach(button => {
        // Add keyboard navigation
        button.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            button.click();
          }
        });
  
        button.addEventListener('click', (e) => {
          const page = parseInt(e.target.getAttribute('data-page'));
          if (page && page !== this.currentPage) {
            const previousPage = this.currentPage;
            this.currentPage = page;
            
            // Re-render with current filters and sorting
            let resources = [...this.originalResources];
            
            // Apply filters
            if (this.currentFilters.board_council !== '') {
              resources = resources.filter(resource => 
                resource.board_council === this.currentFilters.board_council
              );
            }
            
            // Apply sorting
            resources = this.applySort(resources);
            
            this.renderPage(resources, this.currentPage);
            this.updatePagination(resources.length);
            
            // Scroll to top of grid
            this.gridContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Manage focus - set focus to the current page button after re-render
            setTimeout(() => {
              const currentPageButton = this.paginationContainer.querySelector(`[data-page="${this.currentPage}"][aria-current="page"]`);
              if (currentPageButton) {
                currentPageButton.focus();
              }
            }, 150);
          }
        });
      });
    }
  
    /**
     * Renders the complete event list with pagination
     * @param {Array} events - Array of events to render
     * @description Main rendering method that handles empty states, pagination setup,
     * and delegates to renderPage for actual card rendering. Resets to page 1
     * when new events are provided.
     * @returns {void}
     */
    renderEvents(events) {
      //console.log('Rendering events with pagination:', events.length);
      
      if (!this.gridContainer) {
        console.error('Grid container not available for rendering');
        console.error('Attempted to find: #EventGrid .resource-grid--wrapper');
        console.error('Available containers:', document.querySelectorAll('#EventGrid *'));
        return;
      }
  
      if (events.length === 0) {
        //console.log('No events match filters, showing empty message');
        this.gridContainer.innerHTML = `
          <div class="grid__item">
            <p role="status">No events found matching your filters.</p>
          </div>
        `;
        this.updatePagination(0);
        return;
      }
  
      // Reset to first page when filters change
      this.currentPage = 1;
      
      this.updatePagination(events.length);
      this.renderPage(events, this.currentPage);
    }
  
    /**
     * Triggers staggered entrance animations for event cards
     * @private
     * @description Animates cards from opacity 0 to 1 and transforms them from
     * translateY(20px) to translateY(0) with a 50ms stagger delay between cards.
     * Creates a smooth, professional loading animation.
     * @returns {void}
     */
    triggerFilteredCardAnimations() {
      // For filtered cards, use our custom animation
      const newCards = this.gridContainer.querySelectorAll('.grid__item');
      
      newCards.forEach((card, index) => {
        // Add animation with a small delay for staggered effect
        setTimeout(() => {
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
          card.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        }, index * 50); // 50ms delay between each card
      });
    }

    generateDateRange(startDate, endDate) {
      // const startDate = this.formatDate(startDate);
      // const endDate = this.formatDate(endDate);

      const options = {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
        weekday: 'long'
      };

      const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', options);
      if(endDate == '') {
        return formattedStartDate;
      }

      const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', options);

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
    /**
     * Generates HTML for a single event card
     * @param {Object} event - Event object containing name, image, url, etc.
     * @param {number} index - Unique index for accessibility IDs
     * @description Creates accessible HTML structure for an event card with:
     * - Semantic article element with proper ARIA labeling
     * - Responsive image handling with lazy loading
     * - Placeholder fallback for missing images
     * - Screen reader optimized structure
     * - Animation-ready initial state (opacity: 0, transform: translateY(20px))
     * @returns {string} HTML string for the event card
     * @example
     * const cardHTML = this.createEventCardHTML({
     *   name: 'Sample Event',
     *   image: '/path/to/image.jpg',
     *   url: '/event-page'
     * }, 1);
     */
    createEventCardHTML(event, index) {
      const hasImage = event.image && event.image !== '';
      const eventName = event.name || 'Untitled Event';
      const eventUrl = event.url || '#';
      
      const dateRange = this.generateDateRange(event.date, event.end_date);

      return `
        <div class="grid__item" style="opacity: 0; transform: translateY(20px);">
          <article class="grid-view-item event-card text-align--left" data-event-url="${eventUrl}" role="article" aria-labelledby="event-title-${index}">
            <div>
              <div class="grid-view-item-image">
                <div class="event-card-state-badge">
                  <span class="event-card-state-badge__text">${event.state}</span>
                </div>
                <a href="${eventUrl}" 
                   title="View ${eventName}" 
                   class="grid__image grid__image__match" 
                   tabindex="-1" 
                   data-event-card-link
                   aria-label="View ${eventName}">
                  ${hasImage ? `
                    <img src="${event.image}" 
                         alt="${eventName}" 
                         loading="lazy" 
                         data-resource-card-image />
                  ` : `
                    <div class="icon icon--placeholder grid__image" 
                         style="width:100%; height:200px; background:#f0f0f0; display:flex; align-items:center; justify-content:center; color:#999;"
                         role="img"
                         aria-label="No image available for ${eventName}">
                      <span aria-hidden="true">No Image</span>
                    </div>
                  `}
                </a>
              </div>
              
              <div class="event-grid--title">
                <div class="event-grid--title__text">
                    <span class="event-grid--title__text__name">${eventName}</span>
                    <div class="event-grid--title__text__date">
                      ${dateRange}
                    </div>
                </div>
                <a class="text-truncate--line" 
                   href="${eventUrl}" 
                   data-event-card-link
                   id="event-title-${index}"
                   aria-label="View ${eventName}">
                   View Details
                </a>
              </div>
            </div>
          </article>
        </div>
      `;
    }
  
    /**
     * Initializes pagination system and DOM references
     * @private
     * @description Sets up essential DOM element references, configures pagination
     * settings from data attributes, and adds accessibility attributes to the
     * grid container. Must be called before rendering resources.
     * @returns {void}
     */
    initializePagination() {
      this.gridContainer = document.querySelector('#EventGrid .event-grid--wrapper');
      //console.log('Grid container found:', this.gridContainer);
      this.paginationContainer = document.getElementById('event-pagination');
      
      // Add accessibility attributes to grid container
      if (this.gridContainer) {
        if (!this.gridContainer.getAttribute('role')) {
          this.gridContainer.setAttribute('role', 'region');
        }
        if (!this.gridContainer.getAttribute('aria-label') && !this.gridContainer.getAttribute('aria-labelledby')) {
          this.gridContainer.setAttribute('aria-label', 'Event grid');
        }
      }
      
      // Get resources per page from data attribute
      const eventsPerPageAttr = this.gridContainer?.getAttribute('data-events-per-page');
      this.eventsPerPage = eventsPerPageAttr ? parseInt(eventsPerPageAttr) : 16;
      
      //console.log('Pagination initialized with', this.eventsPerPage, 'events per page');
      //console.log('Grid container found:', !!this.gridContainer);
    }
  
    /**
     * Renders all original events without any filters
     * @description Initial rendering method that displays all loaded events
     * with pagination. Handles empty states and error conditions.
     * Called during initialization and when filters are reset.
     * @returns {void}
     */
    renderAllEvents() {
      if (!this.gridContainer) {
        console.error('Grid container not found');
        console.error('Attempted to find: #EventGrid .resource-grid--wrapper');
        console.error('EventGrid element exists:', !!document.querySelector('#EventGrid'));
        return;
      }
  
      //console.log('Rendering all events with pagination:', this.originalEvents.length);
      
      if (this.originalEvents.length === 0) {
        this.gridContainer.innerHTML = `
          <div class="grid__item">
            <p role="status">No events found.</p>
          </div>
        `;
        this.updatePagination(0);
        return;
      }
  
      this.updatePagination(this.originalEvents.length);
      if(CONFIGURATIONS.defaultSort !== 'none') {
        this.currentSort = CONFIGURATIONS.defaultSort;
        const sortedEvents = this.applySort(this.originalEvents);
        this.renderPage(sortedEvents, this.currentPage);
      } else {
        this.renderPage(this.originalEvents, this.currentPage);
      }
    }
  
    /**
     * Utility method to check if a value is a valid date
     * @param {*} value - Value to test for date validity
     * @description Checks if the provided value is either a Date object
     * or a string that can be parsed as a valid date.
     * @returns {boolean} True if value is a valid date, false otherwise
     * @example
     * this.isDate('2024-01-01'); // true
     * this.isDate(new Date());   // true
     * this.isDate('invalid');    // false
     */
    isDate(value) {
      return value instanceof Date || !isNaN(Date.parse(value));
    }

    /**
     * Formats a date string to "Month DD, YYYY" format
     * @param {string|Date} dateInput - Date string or Date object to format
     * @description Converts various date formats to a readable "June 24, 2025" format.
     * Handles common formats like ISO dates, MM/DD/YYYY, YYYY-MM-DD, etc.
     * @returns {string} Formatted date string or original input if invalid
     * @example
     * this.formatDate('2025-06-24'); // "June 24, 2025"
     * this.formatDate('06/24/2025'); // "June 24, 2025"
     * this.formatDate('2025-06-24T10:00:00Z'); // "June 24, 2025"
     */
    formatDate(dateInput) {
      if (!dateInput) return dateInput;
      
      try {
        const date = new Date(dateInput);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return dateInput; // Return original if invalid
        }
        
        // Format to "Month DD, YYYY"
        const options = { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric',
          timeZone: 'UTC' // Avoid timezone issues with date-only strings
        };
        
        return date.toLocaleDateString('en-US', options);
      } catch (error) {
        console.warn('Date formatting error:', error, 'for input:', dateInput);
        return dateInput; // Return original if error
      }
    }
  }
  
  // Initialize the event filter when the DOM is loaded
  document.addEventListener('DOMContentLoaded', async () => {
    //console.log('DOM loaded, initializing EventFilter');
    window.eventFilter = new EventFilter();
  });
  
  // Also initialize if DOM is already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      if (!window.eventFilter) {
        //console.log('DOM already loaded, initializing EventFilter');
        window.eventFilter = new EventFilter();
      }
    });
  } else {
    if (!window.eventFilter) {
      //console.log('Document ready, initializing EventFilter immediately');
      window.eventFilter = new EventFilter();
    }
  }
  